import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { SupplyCategory } from '@ebalance/proto/bfftypes/v1/supply_category_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  Badge,
  Box,
  Link,
  NativeSelectField,
  NativeSelectIndicator,
  NativeSelectRoot,
  RadioButton,
  type SegmentedControlOption,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { DateStepper } from '~/components/common/filters'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import { convertAreaLabel } from '~/models/area'
import { SOURCE_LINK_BY_CATEGORY } from '~/pages/Supply48Data/models/categorySourceLink'
import type { DisplayUnit } from '~/pages/Supply48Data/models/displayUnit'
import { displayUnitLabelKey } from '~/pages/Supply48Data/models/displayUnit'
import { CATEGORY_OPTIONS } from '~/pages/Supply48Data/models/filterOptions'

/**
 * 比較モード (前日比) は spec supply-48-data.md「P2 移管: 比較モード」で P0 対象外
 * (design/supply/48data.html・mock-app とも撤去済) のため持たない。
 */

type Props = {
  /** 売買区分 */
  buySell: Side
  onBuySellChange: (v: Side) => void

  /**
   * カテゴリ (相対取引 / スポット・時間前 / 差金決済。常時BU は相対取引に含む = AC-S4D-001)。
   * 差金決済 (proto 上は FUTURES) は売り・買いの両方に出す区分 (spec「売りのカテゴリタブ:
   * 自社発電、相対取引、スポット・時間前、差金決済」「買いのカテゴリタブ: 相対取引、
   * スポット・時間前、差金決済」)。
   */
  category: SupplyCategory
  onCategoryChange: (v: SupplyCategory) => void

  /** 選択可能なエリア一覧 (personal settings から) */
  availableAreas: AreaCode[]
  selectedArea: AreaCode
  onAreaChange: (v: AreaCode) => void

  /** 対象日 (AC-S4D-002: 単日固定。 日めくりで前日 / 翌日へ 1 日ステップ) */
  targetDate: Date
  onTargetDateChange: (v: Date) => void
  /**
   * ワークフローケース由来で受渡日が固定されているか (issue #7131 /
   * AC-WFW-006a)。 true のとき対象日 DateStepper (前後日 / 日付欄 / 今日) を
   * disabled にする。
   */
  isDateLocked?: boolean
  /**
   * ケース対象日が確定済みか (= `lockedTargetDate !== undefined`)。 true の間
   * だけ Badge「ケース対象日に固定」を表示する (Design Note §States: 取得済の
   * みが対象)。省略時は false。
   */
  isTargetDateConfirmed?: boolean

  /**
   * 表示単位 (AC-S4D-037/038)。既定 kWh/30min で、切替は表示だけに影響する
   * (永続値は kWh/30min 固定)。design/supply/48data.html の `s48-unit-seg` 準拠で
   * サイドバー内に置く。
   */
  displayUnit: DisplayUnit
  onDisplayUnitChange: (v: DisplayUnit) => void
}

/**
 * サイドバーのフィルタラベル (md/bold)。design 48data.html の `.filter-label`
 * (`css/components.css` 実測: `--font-size-md` 14px / 700 / `--text-high-emphasis`)
 * 準拠。 2026-08-06 デザイナー指摘で旧 sm/medium/midEmphasis から是正した
 * (2026-07-12 に見送っていた乖離の解消。 SpotBidding の filter ラベルと同じ形)。
 */
const SidebarFilterLabel = ({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement => (
  // design ルール例外: filter label は Text.highEmphasis 固定 (design/CLAUDE.md)
  <Text textStyle="filterLabel" marginBottom={1} color="Text.highEmphasis">
    {children}
  </Text>
)

export const FilterSidebar = ({
  buySell,
  onBuySellChange,
  category,
  onCategoryChange,
  availableAreas,
  selectedArea,
  onAreaChange,
  targetDate,
  onTargetDateChange,
  displayUnit,
  onDisplayUnitChange,
  isDateLocked = false,
  isTargetDateConfirmed = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { dateLockedReason } = useWorkflowLockReasons()

  // AC-S4D-037: 表示単位。design 準拠で横並びのラジオ (区分は縦リスト) にする
  const unitRadios: Array<{ label: string; value: string }> = [
    { label: t(displayUnitLabelKey('kWh30min')), value: 'kWh30min' },
    { label: t(displayUnitLabelKey('kW')), value: 'kW' },
  ]

  /*
   * カテゴリ選択肢。design/supply/48data.html 準拠でラジオボタン (丸ポチ) の
   * 縦リストとして描画する。
   *
   * AC-S4D-001 (判断 2026-07-31 / #4335): 常時BU は独立タブにせず相対取引カテゴリに
   * 含める。相対取引タブ内で取引種別 (約定経路から導出) として判別・表示するため、
   * 相対取引・スポット・時間前は売り・買いともに同じ選択肢になる。
   *
   * 差金決済 (受渡区分 EEX・TOCOM・ICE・スワップ、proto 上は FUTURES) は売り・買いの
   * 両方に出す (AC-S4D-001、2026-10-07 PdM 判断 / #8500 で旧タブ名「先物」から改称し、
   * 売り側にも表示先を設けた)。
   *
   * 売りの「自社発電」区分は本 categoryRadios に含まれない (未実装の残件)。
   * proto SupplyCategory に SUPPLY_CATEGORY_GENERATION 自体が定義されておらず
   * (BILATERAL/MARKET/BACKUP_REGULAR/FUTURES の 4 値のみ)、契約が無いため本
   * issue #5129 のスコープ外。
   */
  const categoryRadios = CATEGORY_OPTIONS.map((option) => ({
    label: t(option.labelKey),
    value: String(option.value),
  }))

  /**
   * 選択中の区分のデータを管理している画面への導線 (design `supply/48data.html` の
   * `#s48-source-link` + `CAT_SOURCE`)。 48点データは各所から集めた値の**閲覧・微修正**の場で、
   * 元データを直すのは別画面なので、 今どこを見ているかに応じて行き先を出し分ける。
   *
   * design と同じく **管理画面を持たない区分では導線ごと隠す** (リンクが前の区分のまま
   * 残ると別画面への誤誘導になる)。
   *
   * design の `own` / `fit` (発電所別を開く) は proto `SupplyCategory` に自社発電の値が
   * 無く区分自体が未実装のため、 対応表にも入れない (区分が入る時に併せて足す)。
   * `BACKUP_REGULAR` (常時BU) は AC-S4D-001 で相対取引に含める判断なので相対取引を指す。
   */
  const sourceLink = SOURCE_LINK_BY_CATEGORY[category]

  /**
   * 売/買 セグメントのラベルは一覧の行バッジ・フォームと同じ `TradeSideLabel`
   * (色付き pill) で示す (design/supply/48data.html「売/買 の色は ecl-trade-label を
   * 維持」)。DS `SegmentedControlOption.label` は `React.ReactNode` なので
   * radio (= `label: string` 固定) と違い slot を組み直さずそのまま差し込める。
   * グループの accessible name は `AccessibleSegmentedControl` の `aria-label`
   * (= 「売買区分」) が持ち、各ボタンの名前は pill の `aria-label` 由来になる。
   *
   * `as="span"`: DS `SegmentedControl` は選択肢 1 件を `<label>` で包む
   * (実測した DOM: `label > div > div > (ここ)`)。`<label>` は phrasing content
   * しか置けないため、DS 既定の `<p>` のままだと入れ子が不正になる
   * (`TradeSideRadioGroup` と同じ理由)。
   */
  const buySellOptions: Array<SegmentedControlOption<string>> = [
    {
      label: <TradeSideLabel as="span" side={Side.SELL} />,
      value: String(Side.SELL),
    },
    {
      label: <TradeSideLabel as="span" side={Side.BUY} />,
      value: String(Side.BUY),
    },
  ]

  return (
    <VStack
      position="sticky"
      top={2}
      align="stretch"
      flexShrink={0}
      alignSelf="flex-start"
      gap={4}
      overflowY="auto"
      width="260px"
      maxHeight="calc(100vh - 120px)"
      padding={3}
      background="Surface.primary"
      borderWidth={1}
      borderColor="Border.lowEmphasis"
      borderRadius="lg"
    >
      {/* 売/買セグメント */}
      <Box>
        {/* 可視ラベルは**置かない**。 design `supply/48data.html:509-513` の先頭
            `.filter-group` は `.filter-label` を持たず、 売/買 の pill 自体がラベルを
            兼ねている (`.filter-label` が付くのは 区分 / エリア / 対象日 / 表示単位)。
            ここに 1 行置くと左カラムの先頭コントロールだけが 25px (行高 21px +
            marginBottom 4px) 下がり、 右カラム先頭 (バナー / ツールバー) と段差になる
            (デザイナー指摘 2026-08-28「画面左のフィルターの上の余白の段差が気になる」)。
            択一群の accessible name はラッパの role="radiogroup" 側が持つため、 可視
            ラベルを外しても読み上げ名は失われない (DS `SegmentedControl` の a11y 補正
            込み。 詳細は同 component の JSDoc)。 */}
        {/* widthAlignment: design 48data.html の `#s48-buysell-seg` は
            `style="width:100%"` + 各セグメント `flex:1` = サイドバー幅を 2 等分。
            DS 既定の `fit-content` はコンテンツ幅・左寄せになるため明示する
            (`SegmentedControlProps` の既存 prop = DS 差異ではない)。 */}
        <AccessibleSegmentedControl
          aria-label={t('page:supply48Data.filter.buySellLabel')}
          name="buySell"
          onSelect={(v) => onBuySellChange(Number(v) as Side)}
          options={buySellOptions}
          selected={String(buySell)}
          widthAlignment="space-evenly"
        />
      </Box>

      {/* カテゴリ (= design 準拠の横並び + 折返しラジオ) */}
      <Box>
        <SidebarFilterLabel>
          {t('page:supply48Data.filter.categoryLabel')}
        </SidebarFilterLabel>
        <RadioButton
          aria-label={t('page:supply48Data.filter.categoryLabel')}
          // **横並び + 折返し**にする (デザイナー指摘 2026-08-28「区分の項目は 8080 の
          // ように増えると思うので、 8080 同様、 横にも並ぶようにしたほうが縦幅が
          // 増えない」)。 design は `supply/48data.html:515` の
          // `#s48-cat-tabs { display:flex; flex-wrap:wrap; }` で、 区分は 6 つある
          // (自社発電 / FIT / 相対取引 / スポット・時間前 / 先物 / 蓄電池)。
          // app は自社発電と FIT が未実装で今は 4 つだが、 入ると縦リストでは 6 行に
          // なりサイドバーの縦を圧迫する。
          //
          // ecloud RadioButton の内部 Stack は flex-wrap を props から変更できないため
          // nested selector で上書きする。 gap も同じ経路で、 DS 既定 16px
          // (Chromium 実測) に対し design は 8px
          // (2026-08-28 デザイナー指摘「区分の radio 間の gap は 8px」)。
          css={{ '& .chakra-stack': { flexWrap: 'wrap', gap: '8px' } }}
          name="category"
          onValueChange={(details) => {
            if (details.value !== null) {
              onCategoryChange(Number(details.value) as SupplyCategory)
            }
          }}
          radios={categoryRadios}
          value={String(category)}
        />
        {sourceLink !== undefined && (
          <Link
            display="inline-flex"
            // 上余白は 8px (2026-08-28 デザイナー指摘)。 chakra の spacing token は
            // 4px 刻みなので 2 = 8px。 区分ラジオの gap と同値にして、
            // 最後のラジオとの間隔がリスト内の間隔と揃うようにする。
            marginTop={2}
            fontSize="md"
            asChild
          >
            <RouterLink to={sourceLink.path}>
              {t(sourceLink.labelKey)}
            </RouterLink>
          </Link>
        )}
      </Box>

      {/* エリア (= design 準拠のセレクトボックス。選択肢は業務エリア設定由来のみ) */}
      <Box>
        <SidebarFilterLabel>
          {t('page:supply48Data.filter.areaLabel')}
        </SidebarFilterLabel>
        {/*
          size は他画面の filter select (SpotBidding の NormalBidTab / BlockBidTab)
          と同じ md。 design 48data.html の `.ecl-select--sm` は名前に反して
          height 40px / font-size 14px (`css/components.css` 実測) で、 これは chakra の
          nativeSelect size=md (`--select-field-height: sizes.10` = 40px) に相当する。
          sm は 36px (sizes.9) なので 1 段小さかった。

          fontSize を明示するのは、 chakra 既定 recipe が size=md の field に
          `textStyle: 'sm'` を当てており、 本 app では textStyle が解決されず
          ecloud の fontSizes.sm (= 12px) に落ちるため (`~/constants/system.ts` の
          dsFallbackFixRecipes と同型の事象)。 DS `Input` は `textStyle="textMd"`
          (= 14px) を持つので、 DS 実装値でも design でも 14px が正。
        */}
        <NativeSelectRoot size="md">
          <NativeSelectField
            fontSize="md"
            aria-label={t('page:supply48Data.filter.areaLabel')}
            onChange={(e) => onAreaChange(Number(e.target.value) as AreaCode)}
            value={String(selectedArea)}
          >
            {/* 個人設定ロード完了までの過渡状態 (UNSPECIFIED) 用の空 option */}
            {selectedArea === AreaCode.UNSPECIFIED && (
              <option disabled value={String(AreaCode.UNSPECIFIED)} />
            )}
            {availableAreas.map((areaCode) => (
              <option key={areaCode} value={String(areaCode)}>
                {convertAreaLabel(areaCode)}
              </option>
            ))}
          </NativeSelectField>
          <NativeSelectIndicator />
        </NativeSelectRoot>
      </Box>

      {/*
        対象日 (= design 準拠の単日 date input + 日めくりステッパー)。
        AC-S4D-002 で単日固定のため期間 (DateRangePicker) / 期間プリセットは持たない。
      */}
      <Box>
        <SidebarFilterLabel>
          {t('page:supply48Data.filter.dateLabel')}
        </SidebarFilterLabel>
        <DateStepper
          dateAriaLabel={t('page:supply48Data.filter.dateLabel')}
          disabled={isDateLocked}
          disabledReason={dateLockedReason}
          layout="stacked"
          onChange={onTargetDateChange}
          value={targetDate}
        />
        {isTargetDateConfirmed && (
          <Badge marginTop={1} status="info">
            {t('common:filter.caseLockedBadge')}
          </Badge>
        )}
      </Box>

      {/*
        単位 (AC-S4D-037/038)。design/supply/48data.html の `s48-unit-seg` と同じく
        対象日の下に横並びラジオで置く。表示だけの切替で、保存値は kWh/30min 固定。
      */}
      <Box>
        <SidebarFilterLabel>
          {t('page:supply48Data.filter.unitLabel')}
        </SidebarFilterLabel>
        <RadioButton
          aria-label={t('page:supply48Data.filter.unitLabel')}
          name="supply48DisplayUnit"
          onValueChange={(details) => {
            if (details.value !== null) {
              onDisplayUnitChange(details.value as DisplayUnit)
            }
          }}
          radios={unitRadios}
          value={displayUnit}
        />
      </Box>
    </VStack>
  )
}
